//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import React, { useState } from 'react';

import DropdownButton from '@components/molecules/button-popover/DropdownButton';
import DescriptionAndValidationWrapper, {
  IDescriptionAndValidationProps,
} from '@components/molecules/description-and-validation-wrapper/DescriptionAndValidationWrapper';
import Popover, {
  Props as PopoverProps,
} from '@components/molecules/popover/Popover';
import { useUniqueId } from '@hooks/useUniqueId';

type ButtonProps = Pick<
  React.ComponentProps<typeof DropdownButton>,
  | 'placeholderText'
  | 'rightContent'
  | 'isDropdown'
  | 'type'
  | 'trackingId'
  | 'isDisabled'
  | 'valueText'
>;

type Props = IDescriptionAndValidationProps &
  ButtonProps &
  Omit<PopoverProps, 'shouldShowArrow' | 'shouldOpenOnHover' | 'target'>;

/**
 * The ButtonPopover molecule renders a prestyled button with the given
 * selected or placeholder text, and either a chevron or the given content on the right.
 * On click, the button opens a Popover which displays the given contents.
 *
 * The Button popover also includes our standard form context UI: error text, help text, and a label.
 */
const ButtonPopover = (
  {
    className,
    errorText,
    hasError = false,
    helpText,
    isDisabled,
    label,
    labelHelpText,
    placeholderText,
    rightContent,
    trackingId,
    type,
    valueText,
    ...popoverProps
  }: Props
) => {
  const buttonId = useUniqueId('button-popover-button-id');
  const [isRightContentHovered, setIsRightContentHovered] = useState(false);

  const content = rightContent && (
    <div
      onMouseEnter={() => setIsRightContentHovered(true)}
      onMouseLeave={() => setIsRightContentHovered(false)}
    >
      {rightContent}
    </div>
  );

  const renderPopover = (descriptionTextId?: string) => (
    <Popover
      shouldOpenOnHover={false}
      shouldShowArrow={false}
      isDisabled={isRightContentHovered || isDisabled}
      target={
        <DropdownButton
          aria-describedby={descriptionTextId}
          aria-invalid={hasError}
          hasError={hasError}
          id={buttonId}
          isDisabled={isDisabled}
          isDropdown={!rightContent}
          placeholderText={placeholderText}
          rightContent={content}
          trackingId={trackingId}
          type={type}
          valueText={valueText}
        />
      }
      {...popoverProps}
    />
  );

  return (
    <DescriptionAndValidationWrapper
      className={className}
      errorText={errorText}
      formComponentId={buttonId}
      hasError={hasError}
      helpText={helpText}
      label={label}
      labelHelpText={labelHelpText}
      renderFormComponent={renderPopover}
    />
  );
};

export default ButtonPopover;
